/** Shapes KPI snapshots into what the Executive board renders (CEO-01 … CEO-05). Pure functions, unit-tested. */
import type { KpiDefinition, Snapshot } from "./api";
import { STATUS_ORDER, deviationPct, type Status } from "./format";

export type Point = {
  period: string;
  value: number | null;
  target: number | null;
  status: Status;
  last_updated: string;
  refresh_class: string;
};

export type Series = {
  key: string;
  kpi: string;
  company: string;
  plant: string;
  points: Point[]; // ascending by period
};

export const seriesKey = (kpi: string, company: string, plant: string) => `${kpi}|${company}|${plant}`;

export function buildSeries(tiles: Snapshot[]): Series[] {
  const map = new Map<string, Series>();
  for (const t of tiles) {
    const key = seriesKey(t.kpi, t.company, t.plant);
    let s = map.get(key);
    if (!s) {
      s = { key, kpi: t.kpi, company: t.company, plant: t.plant, points: [] };
      map.set(key, s);
    }
    s.points.push({ period: t.period, value: t.value, target: t.target, status: t.status, last_updated: t.last_updated, refresh_class: t.refresh_class });
  }
  for (const s of map.values()) s.points.sort((a, b) => a.period.localeCompare(b.period));
  return [...map.values()];
}

/** Distinct periods, newest first. */
export function periodsOf(tiles: Snapshot[]): string[] {
  return [...new Set(tiles.map((t) => t.period))].sort((a, b) => b.localeCompare(a));
}

export function companiesOf(tiles: Snapshot[]): string[] {
  return [...new Set(tiles.map((t) => t.company).filter(Boolean))].sort((a, b) => a.localeCompare(b));
}

export function plantsOf(tiles: Snapshot[], company?: string): string[] {
  return [...new Set(tiles.filter((t) => t.plant && (!company || t.company === company)).map((t) => t.plant))].sort();
}

/** The point for `period`, else the latest point not after it (so a KPI refreshed weekly still shows). */
export function pointAt(series: Series, period: string): Point | null {
  let best: Point | null = null;
  for (const p of series.points) {
    if (p.period === period) return p;
    if (p.period < period && (!best || p.period > best.period)) best = p;
  }
  return best;
}

export type Tile = {
  series: Series;
  point: Point;
  definition?: KpiDefinition;
  deviation: number | null;
  /** Positive when the movement is favourable given the KPI direction. */
  favourable: boolean | null;
  prior: Point | null;
};

export function buildTiles(series: Series[], period: string, defs: KpiDefinition[], filter: { company?: string; plant?: string }): Tile[] {
  const byCode = new Map(defs.map((d) => [d.name, d]));
  const tiles: Tile[] = [];
  for (const s of series) {
    if (filter.company && s.company !== filter.company) continue;
    if (filter.plant && s.plant !== filter.plant) continue;
    const point = pointAt(s, period);
    if (!point) continue;
    const definition = byCode.get(s.kpi);
    const deviation = deviationPct(point.value, point.target);
    const higherIsBetter = (definition?.direction ?? "Higher is better") !== "Lower is better";
    const favourable = deviation == null ? null : higherIsBetter ? deviation >= 0 : deviation <= 0;
    const idx = s.points.indexOf(point);
    const prior = idx > 0 ? s.points[idx - 1] : null;
    tiles.push({ series: s, point, definition, deviation, favourable, prior });
  }
  tiles.sort((a, b) => {
    const st = STATUS_ORDER[a.point.status] - STATUS_ORDER[b.point.status];
    if (st !== 0) return st;
    return Math.abs(b.deviation ?? 0) - Math.abs(a.deviation ?? 0);
  });
  return tiles;
}

export function countByStatus(tiles: Tile[]): Record<Status, number> {
  const out: Record<Status, number> = { Green: 0, Amber: 0, Red: 0, Grey: 0 };
  for (const t of tiles) out[t.point.status] += 1;
  return out;
}

/** Most recent refresh among the tiles shown (freshness stamp, requirement 9.6). */
export function latestUpdate(tiles: Tile[]): string | null {
  let latest: string | null = null;
  for (const t of tiles) if (!latest || t.point.last_updated > latest) latest = t.point.last_updated;
  return latest;
}
